/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useRef, useEffect } from 'react';
import { Upload, FileText, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuthStore } from '../stores/authStore';
import Layout from '../components/layout/Layout';
import ExportModal from '../components/csv/ExportModal';
import RegenerateModal from '../components/csv/RegenerateModal';
import BatchRegenerateModal from '../components/csv/BatchRegenerateModal';
import BatchProgressModal from '../components/csv/BatchProgressModal';
import UploadStep from '../components/csv/UploadStep';
import MappingStep from '../components/csv/MappingStep';
import EditorStep from '../components/csv/EditorStep';
import { parseCSV, autoDetectMapping, CSVRow, CSVData } from '../utils/csvUtils';

const CSVEditorPage: React.FC = () => {
  const { user } = useAuthStore();
  const [currentStep, setCurrentStep] = useState<'upload' | 'mapping' | 'editor'>('upload');
  const [hasVisitedEditor, setHasVisitedEditor] = useState(false);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvData, setCsvData] = useState<CSVData | null>(null);
  const [columnMapping, setColumnMapping] = useState({
    filename: '',
    title: '',
    description: '',
    keywords: ''
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [regeneratingIndices, setRegeneratingIndices] = useState<Set<number>>(new Set());
  const [isBatchRegenerating, setIsBatchRegenerating] = useState(false);
  const [batchProgress, setBatchProgress] = useState<{
    currentBatch: number;
    totalBatches: number;
    processedItems: number;
    totalItems: number;
    successCount: number;
    errorCount: number;
    failedRows: number[];
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Regeneration settings modal
  const [showRegenerateSettings, setShowRegenerateSettings] = useState(false);
  const [regenerateSettings, setRegenerateSettings] = useState({
    titleLength: 70,
    descriptionLength: 150,
    keywordsCount: 30,
    customInstructions: ''
  });
  const [pendingRegenerateIndex, setPendingRegenerateIndex] = useState<number | null>(null);

  // Batch regeneration modal
  const [showBatchRegenerateModal, setShowBatchRegenerateModal] = useState(false);
  const [userCredits, setUserCredits] = useState<number | null>(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // Export modal and field selection
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFieldSelection, setExportFieldSelection] = useState<{ [key: string]: boolean }>({});

  // Export loading state
  const [isExporting, setIsExporting] = useState(false);

  // Separate loading states for different credit checks
  const [isCheckingCreditsForBatch, setIsCheckingCreditsForBatch] = useState(false);
  const [isCheckingCreditsForIndividual, setIsCheckingCreditsForIndividual] = useState(false);
  const [isCheckingCreditsForExport, setIsCheckingCreditsForExport] = useState(false);

  // Export always costs 1 credit - removed free export logic

  // 🛡️ FORCE PROTECTION: Always protect step 3 (editor) to prevent data loss
  useEffect(() => {
    // Use direct window event assignment for better reliability
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // FORCE PROTECTION: Always protect step 3 (editor)
      if (currentStep === 'editor') {
        // Try multiple approaches for maximum compatibility
        e.preventDefault();
        e.returnValue = 'You are editing CSV data. Are you sure you want to leave? All changes will be lost!';
        return 'You are editing CSV data. Are you sure you want to leave? All changes will be lost!';
      }
    };

    const handlePopState = (e: PopStateEvent) => {
      // FORCE PROTECTION: Always protect step 3 (editor)
      if (currentStep === 'editor') {
        const confirmLeave = window.confirm(
          'You are editing CSV data. Are you sure you want to go back? All changes will be lost!'
        );

        if (!confirmLeave) {
          // Prevent the navigation
          window.history.pushState(null, '', window.location.href);
          e.preventDefault();
          return false;
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload, { capture: true });
    window.addEventListener('popstate', handlePopState, { capture: true });

    // Also set direct window properties as fallback
    window.onbeforeunload = handleBeforeUnload;

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload, { capture: true });
      window.removeEventListener('popstate', handlePopState, { capture: true });
      window.onbeforeunload = null;
    };
  }, [currentStep]);

  // Additional protection: Override React Router navigation
  useEffect(() => {
    if (currentStep === 'editor') {
      // Override history methods
      const originalPushState = window.history.pushState;
      const originalReplaceState = window.history.replaceState;

      window.history.pushState = function(state, title, url) {
        if (url && typeof url === 'string' && !url.includes('/csv-editor')) {
          const confirmLeave = window.confirm(
            'You are editing CSV data. Are you sure you want to navigate away? All changes will be lost!'
          );
          if (!confirmLeave) {
            return;
          }
        }
        return originalPushState.call(this, state, title, url);
      };

      window.history.replaceState = function(state, title, url) {
        return originalReplaceState.call(this, state, title, url);
      };

      return () => {
        window.history.pushState = originalPushState;
        window.history.replaceState = originalReplaceState;
      };
    }
  }, [currentStep]);

  // Handle online/offline status for batch processing
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [isBatchRegenerating]);

  // Save batch progress to localStorage for potential resume
  useEffect(() => {
    if (isBatchRegenerating && batchProgress) {
      const progressData = {
        csvFile: csvFile?.name,
        progress: batchProgress,
        timestamp: Date.now()
      };
      localStorage.setItem('csvBatchProgress', JSON.stringify(progressData));
    } else if (!isBatchRegenerating) {
      localStorage.removeItem('csvBatchProgress');
    }
  }, [isBatchRegenerating, batchProgress, csvFile]);

  // Step 1: File Upload Handler
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }

    // Validate file size (10MB limit)
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
    if (file.size > MAX_FILE_SIZE) {
      toast.error(`File too large (${(file.size / 1024 / 1024).toFixed(1)}MB). Maximum size: 10MB`);
      return;
    }

    setCsvFile(file);
    setIsProcessing(true);

    try {
      const text = await file.text();
      const parsedData = parseCSV(text);

      // Validate row count (1000 rows max)
      const MAX_ROWS = 1000;
      const LARGE_DATASET_THRESHOLD = 500;

      if (parsedData.rows.length > MAX_ROWS) {
        toast.error(`CSV too large (${parsedData.rows.length} rows). Maximum: 1,000 rows`);
        return;
      }

      // Warning for large datasets
      if (parsedData.rows.length > LARGE_DATASET_THRESHOLD) {
        toast.warning(`⚠️ Large dataset detected (${parsedData.rows.length} rows). Batch operations may be slower. Consider using smaller files for better performance.`, {
          duration: 6000 // Show for 6 seconds
        });
      }

      // Validate minimum rows
      if (parsedData.rows.length === 0) {
        toast.error('CSV file appears to be empty or invalid');
        return;
      }

      // Validate headers (minimum 4 columns)
      if (parsedData.headers.length < 4) {
        toast.error('CSV must have at least 4 columns (Filename, Title, Description, Keywords)');
        return;
      }

      setCsvData({
        headers: parsedData.headers,
        rows: [], // Will be populated after mapping
        mappedHeaders: {}
      });

      // Auto-detect column mapping
      const autoMapping = autoDetectMapping(parsedData.headers);
      setColumnMapping(autoMapping);

      setCurrentStep('mapping');

      // Show success message with auto-detection info
      const detectedFields = Object.entries(autoMapping).filter(([, value]) => value !== '').length;
      if (detectedFields > 0) {
        toast.success(`CSV uploaded successfully! ${parsedData.rows.length} rows found. Auto-detected ${detectedFields} field(s).`);
      } else {
        toast.success(`CSV uploaded successfully! ${parsedData.rows.length} rows found. Please map the columns manually.`);
      }
    } catch (error: any) {
      toast.error(`Failed to read CSV file: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  // Step 2: Column Mapping Handler
  const handleMappingComplete = async () => {
    if (!csvData || !csvFile) return;

    const { filename, title, description, keywords } = columnMapping;
    if (!filename || !title || !description || !keywords) {
      toast.error('Please map all required columns');
      return;
    }

    setIsProcessing(true);

    try {
      const text = await csvFile.text();
      const parsedData = parseCSV(text);

      const filenameIndex = parsedData.headers.indexOf(filename);
      const titleIndex = parsedData.headers.indexOf(title);
      const descriptionIndex = parsedData.headers.indexOf(description);
      const keywordsIndex = parsedData.headers.indexOf(keywords);

      if (filenameIndex === -1 || titleIndex === -1 || descriptionIndex === -1 || keywordsIndex === -1) {
        throw new Error('One or more selected columns not found in CSV');
      }

      const rows: CSVRow[] = parsedData.rows.map((rowData, idx) => {
        // Start with standard fields
        const row: CSVRow = {
          filename: rowData[filenameIndex] || `Row ${idx + 1}`, // Fallback filename if empty
          title: rowData[titleIndex] || '',
          description: rowData[descriptionIndex] || '',
          keywords: rowData[keywordsIndex] || ''
        };

        // Add all other fields from the CSV
        parsedData.headers.forEach((header, headerIndex) => {
          if (!['filename', 'title', 'description', 'keywords'].includes(header.toLowerCase())) {
            row[header] = rowData[headerIndex] || '';
          }
        });

        return row;
      });

      // Create mapping object for export
      const mappedHeaders: { [key: string]: string } = {};
      if (filename) mappedHeaders.filename = filename;
      if (title) mappedHeaders.title = title;
      if (description) mappedHeaders.description = description;
      if (keywords) mappedHeaders.keywords = keywords;

      setCsvData({
        headers: parsedData.headers,
        rows,
        mappedHeaders
      });

      setCurrentStep('editor');
      setHasVisitedEditor(true); // Mark that we've visited editor step
      toast.success(`Loaded ${rows.length} rows from CSV!`);
    } catch (error: any) {
      toast.error(`Failed to process CSV data: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  // Update CSV data
  const handleUpdateCSVData = (index: number, field: string, value: string) => {
    if (!csvData) return;

    const newRows = [...csvData.rows];
    newRows[index][field] = value;
    setCsvData({ ...csvData, rows: newRows });

    // Mark as having unsaved changes to warn user before leaving
  };

  // Handle batch operations for all rows
  const handleBatchUpdateAll = (updatedRows: CSVRow[]) => {
    if (!csvData) return;
    setCsvData({ ...csvData, headers: csvData.headers, rows: updatedRows });
    // Mark as having unsaved changes to warn user before leaving
  };

  // Show export modal and initialize field selection
  const handleExportClick = () => {
    if (!csvData || !csvData.rows.length) {
      toast.error('No data to export');
      return;
    }

    // Initialize all fields as selected by default
    const fieldSelection: { [key: string]: boolean } = {};
    csvData.headers.forEach(header => {
      fieldSelection[header] = true;
    });

    setExportFieldSelection(fieldSelection);
    setShowExportModal(true);
  };

  // Export CSV with current data and field selection
  const handleExportCSV = async () => {
    // ...existing code...

    if (!csvData || !csvData.rows.length) {
      console.error('❌ [FRONTEND] No CSV data available for export');
      toast.error('No data to export');
      return;
    }

  // ...existing code...

    // Check authentication
    if (!user?.id) {
      console.error('❌ [FRONTEND] User not authenticated');
      toast.error('User not authenticated');
      return;
    }

    // Safety check: ensure batch regeneration is not active
    if (isBatchRegenerating) {
      console.warn('⚠️ [FRONTEND] Batch regeneration is active, cannot export. Current state:', {
        isBatchRegenerating,
        batchProgress
      });
      toast.error('Cannot export while batch regeneration is in progress');
      return;
    }

    // Export always costs 1 credit
    try {
      setIsExporting(true);
      setIsCheckingCreditsForExport(true);

        // Get user's API key
        const apiKeyService = (await import('../services/apiKeyService')).default;
        const { keys: apiKeys } = await apiKeyService.getApiKeys();

        const activeApiKey = apiKeys.find(k => k.is_active);
        if (!activeApiKey || !activeApiKey.api_key) {
          console.error('❌ [FRONTEND] No active API key found');
          toast.error('No active API key found. Please create an API key in your account settings.');
          return;
        }

        // Call a simple API to deduct 1 credit for export (you might need to create this endpoint)
        const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'https://api.metapic.app';

        // For now, we'll use a simple approach - charge via the regenerate endpoint with dummy data
        // Ideally, you'd want a dedicated endpoint for export charges
        if (!user?.id) {
          console.error('❌ [FRONTEND] User authentication lost during process');
          toast.error('User not authenticated');
          return;
        }

        const chargeRequest = {
          // Required fields for improve-metadata endpoint
          title: 'CSV Export Charge',
          keywords: ['export'],
          // Special marker for CSV export - put in customInstructions
          customInstructions: 'CSV_EXPORT_CHARGE: Export 1 CSV file',
          // Use settings field as expected by improve-metadata endpoint
          settings: {
            title_length: 20,
            description_length: 50,
            keywords_count: 10  // Must be between 10 and 100, not 1
          }
        };

        const response = await fetch(`${apiBaseUrl}/api/v1/improve-metadata`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': activeApiKey.api_key
          },
          body: JSON.stringify(chargeRequest)
        });
        // ...existing code...

        if (!response.ok) {
          const errorText = await response.text();
          console.error('❌ [FRONTEND] Backend returned error response:', {
            status: response.status,
            statusText: response.statusText,
            errorText: errorText.substring(0, 500),
            fullErrorLength: errorText.length
          });

          if (response.status === 402 || errorText.includes('insufficient credits')) {
            toast.error('❌ Insufficient credits! You need 1 credit to export.', {
              duration: 5000,
              action: {
                label: 'Buy Credits',
                onClick: () => window.open('/pricing', '_blank')
              }
            });
            return;
          }
          throw new Error(`Export charge failed: ${response.status}`);
        }

        toast.success('💎 1 credit charged for CSV export');

      } catch (error: any) {
        console.error('Export payment failed:', error);
        toast.error(`Failed to process export payment: ${error.message}`);
        return;
      } finally {
  setIsCheckingCreditsForExport(false);
  setIsExporting(false);
      }

    try {
      // Filter headers based on user selection
      const selectedHeaders = csvData.headers.filter(header => exportFieldSelection[header]);

      if (selectedHeaders.length === 0) {
        toast.error('Please select at least one field to export');
        return;
      }

      // Create CSV content with selected headers only
      const headers = selectedHeaders.join(',');

      // Map each row to CSV format, only including selected fields
      const rows = csvData.rows.map(row => {
        return selectedHeaders.map(header => {
          let value = '';

          // Map header to our internal field names (case-insensitive)
          const headerLower = header.toLowerCase().trim();

          // Check if this header is one of our standard mapped fields
          if (csvData.mappedHeaders) {
            if (csvData.mappedHeaders.filename === header) {
              value = row.filename;
            } else if (csvData.mappedHeaders.title === header) {
              value = row.title;
            } else if (csvData.mappedHeaders.description === header) {
              value = row.description;
            } else if (csvData.mappedHeaders.keywords === header) {
              value = row.keywords;
            } else {
              // For unmapped/custom fields, use the exact header name as key
              value = row[header] || '';
            }
          } else {
            // Fallback: check by pattern matching
            if (headerLower.includes('filename') || headerLower === 'name') {
              value = row.filename;
            } else if (headerLower.includes('title') || headerLower.includes('headline') || headerLower.includes('caption')) {
              value = row.title;
            } else if (headerLower.includes('description') || headerLower.includes('desc') || headerLower.includes('content')) {
              value = row.description;
            } else if (headerLower.includes('keywords') || headerLower.includes('keyword') || headerLower.includes('tags')) {
              value = row.keywords;
            } else {
              // For additional/custom fields, use exact header match
              value = row[header] || '';
            }
          }

          // Escape quotes and wrap in quotes to handle commas and special chars
          const escapedValue = value.replace(/"/g, '""');
          return `"${escapedValue}"`;
        }).join(',');
      });

      const csvContent = [headers, ...rows].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      const timestamp = new Date().toISOString().split('T')[0];

      link.setAttribute('href', url);
      link.setAttribute('download', `edited_metadata_${timestamp}.csv`);
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up
      URL.revokeObjectURL(url);

      // Close modal
      setShowExportModal(false);

      // Mark changes as "saved" (user has exported)

      // Show success message
      toast.success(`CSV exported successfully! ${csvData.rows.length} rows with ${selectedHeaders.length} fields saved.`);
    } catch (error) {
      toast.error('Failed to export CSV. Please try again.');
    }
  };

  // Check credits before showing individual regeneration modal
  const checkCreditsAndShowRegenerateModal = async (index: number) => {
    if (!csvData) return;

    setIsCheckingCreditsForIndividual(true);
    try {
      // Get user's credit balance
      const apiKeyService = (await import('../services/apiKeyService')).default;
      const { keys: apiKeys } = await apiKeyService.getApiKeys();

      const activeApiKey = apiKeys.find(k => k.is_active);
      if (!activeApiKey || !activeApiKey.api_key) {
        toast.error('No active API key found. Please create an API key in your account settings.');
        return;
      }

      // Fetch credit balance from API
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'https://api.metapic.app';

      let response;
      // Method 1: Current API endpoint with X-API-Key
      response = await fetch(`${apiBaseUrl}/api/v1/credits/balance`, {
        headers: {
          'X-API-Key': activeApiKey.api_key,
          'Content-Type': 'application/json'
        }
      });

      // Method 2: If fails, try with direct API key as header name
      if (!response.ok) {
        response = await fetch(`${apiBaseUrl}/api/v1/credits/balance`, {
          headers: {
            [activeApiKey.api_key]: '',
            'Content-Type': 'application/json'
          }
        });
      }

      // Method 3: If still fails, try legacy endpoint with Authorization header
      if (!response.ok) {
        response = await fetch(`${apiBaseUrl}/credits/balance`, {
          headers: {
            'Authorization': `Bearer ${activeApiKey.api_key}`,
            'Content-Type': 'application/json'
          }
        });
      }

      if (response.ok) {
        const creditData = await response.json();

        // Handle both single and double nested response structures
        let balance = 0;
        if (creditData.data?.data?.balance !== undefined) {
          // Double nested: data.data.balance
          balance = creditData.data.data.balance;
        } else if (creditData.data?.balance !== undefined) {
          // Single nested: data.balance
          balance = creditData.data.balance;
        } else if (creditData.balance !== undefined) {
          // Direct: balance
          balance = creditData.balance;
        }

        // Check if user has at least 1 credit for individual regeneration
        if (balance >= 1) {
          // Show settings modal if credit is sufficient
          setPendingRegenerateIndex(index);
          setShowRegenerateSettings(true);
        } else {
          // Show insufficient credits modal
          toast.error('❌ Insufficient credits! You need at least 1 credit to regenerate metadata.', {
            duration: 5000,
            action: {
              label: 'Buy Credits',
              onClick: () => window.open('/pricing', '_blank')
            }
          });
        }
      } else {
        // If can't fetch credits, assume they have enough for now and show modal
        setPendingRegenerateIndex(index);
        setShowRegenerateSettings(true);
      }
    } catch (error) {
      // If error checking credits, still show modal but warn user
      setPendingRegenerateIndex(index);
      setShowRegenerateSettings(true);
      toast.warning('Could not check credit balance. Proceeding with regeneration...', {
        duration: 3000
      });
    } finally {
      setIsCheckingCreditsForIndividual(false);
    }
  };

  // Step 3: Individual Re-generate Handler
  const handleRegenerate = (index: number) => {
    if (!csvData) return;

    // Check credits first before showing settings modal
    checkCreditsAndShowRegenerateModal(index);
  };

  // Handle regeneration after settings are confirmed
  const handleConfirmRegenerate = async () => {
    if (pendingRegenerateIndex === null || !csvData) return;

    const index = pendingRegenerateIndex;
    const row = csvData.rows[index];

    // Close modal and clear pending state
    setShowRegenerateSettings(false);
    setPendingRegenerateIndex(null);

    // Start regeneration
    setRegeneratingIndices(prev => new Set(prev).add(index));

    // Mark that user has used AI generation in this session
  // ...existing code...

    try {
      // Get user's API key
      const apiKeyService = (await import('../services/apiKeyService')).default;
      const { keys: apiKeys } = await apiKeyService.getApiKeys();

      const activeApiKey = apiKeys.find(k => k.is_active);
      if (!activeApiKey || !activeApiKey.api_key) {
        throw new Error('No active API key found. Please create an API key in your account settings.');
      }

      // 🚀 Use SAME endpoint as CompleteCard (improve-metadata, not improve-meta-1)

      // Get current metadata to send as input (same as CompleteCard)
      if (!row.title || !row.keywords) {
        throw new Error('Cannot regenerate: missing current metadata (title and keywords required)');
      }

      const regenerateRequest = {
        title: row.title,
        keywords: row.keywords.split(',').map(k => k.trim()),
        // albumId: null, // Remove albumId for CSV editor to use improve_meta_1 path
        customInstructions: regenerateSettings.customInstructions || undefined,
        settings: {
          title_length: regenerateSettings.titleLength,
          description_length: regenerateSettings.descriptionLength,
          keywords_count: regenerateSettings.keywordsCount
        }
      };

      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'https://api.metapic.app';
      const response = await fetch(`${apiBaseUrl}/api/v1/improve-metadata`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': activeApiKey.api_key // ✅ Use actual API key, not JWT token
        },
        body: JSON.stringify(regenerateRequest)
      });

      if (!response.ok) {
        throw new Error(`Regeneration failed: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error?.message || 'Regeneration failed');
      }

      // Validate response structure (same as CompleteCard)
      const responseData = result.data?.data || result.data; // Handle both nested and flat structure
      const aiMetadata = responseData?.metadata;

      if (!aiMetadata || !aiMetadata.title || !aiMetadata.description || !aiMetadata.keywords) {
        throw new Error('AI engine returned incomplete metadata. Please try again.');
      }

      // Update CSV data with new metadata (same structure as CompleteCard)
      const newRows = [...csvData.rows];
      newRows[index] = {
        ...newRows[index],
        title: aiMetadata.title,
        description: aiMetadata.description,
        keywords: Array.isArray(aiMetadata.keywords)
          ? aiMetadata.keywords.join(', ')
          : aiMetadata.keywords
      };

      setCsvData({ ...csvData, rows: newRows });

      // Mark as having unsaved changes after regeneration

      toast.success(`✅ Regenerated metadata for ${row.filename}`);

    } catch (error: any) {
      toast.error(`Failed to regenerate metadata: ${error.message}`);
    } finally {
      setRegeneratingIndices(prev => {
        const newSet = new Set(prev);
        newSet.delete(index);
        return newSet;
      });
    }
  };

  // Check user credits before showing batch regenerate modal
  const checkCreditsAndShowModal = async () => {
  // ...existing code...
    if (!csvData) {
      console.warn('❌ No CSV data available');
      return;
    }

    setIsCheckingCreditsForBatch(true);
    try {
      // Get user's credit balance
      const apiKeyService = (await import('../services/apiKeyService')).default;
      const { keys: apiKeys } = await apiKeyService.getApiKeys();

      const activeApiKey = apiKeys.find(k => k.is_active);
      if (!activeApiKey || !activeApiKey.api_key) {
        toast.error('No active API key found. Please create an API key in your account settings.');
        return;
      }

      // Fetch credit balance from API
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'https://api.metapic.app';

      // Try different header formats and endpoints for API key authentication
      let response;

      // Method 1: Current API endpoint with X-API-Key
      response = await fetch(`${apiBaseUrl}/api/v1/credits/balance`, {
        headers: {
          'X-API-Key': activeApiKey.api_key,
          'Content-Type': 'application/json'
        }
      });

      // Method 2: If fails, try with direct API key as header name
      if (!response.ok) {
        response = await fetch(`${apiBaseUrl}/api/v1/credits/balance`, {
          headers: {
            [activeApiKey.api_key]: '', // API key as header name
            'Content-Type': 'application/json'
          }
        });
      }

      // Method 3: If still fails, try legacy endpoint with Authorization header
      if (!response.ok) {
        response = await fetch(`${apiBaseUrl}/credits/balance`, {
          headers: {
            'Authorization': `Bearer ${activeApiKey.api_key}`,
            'Content-Type': 'application/json'
          }
        });
      }

      if (response.ok) {
        const creditData = await response.json();

        // Handle both single and double nested response structures
        let balance = 0;
        if (creditData.data?.data?.balance !== undefined) {
          // Double nested: data.data.balance
          balance = creditData.data.data.balance;
        } else if (creditData.data?.balance !== undefined) {
          // Single nested: data.balance
          balance = creditData.data.balance;
        } else if (creditData.balance !== undefined) {
          // Direct: balance
          balance = creditData.balance;
        }

        setUserCredits(balance);
      } else {
        // If can't fetch credits, assume they have enough for now
        setUserCredits(null);
      }

      setShowBatchRegenerateModal(true);
    } catch (error) {
      // If error checking credits, still show modal but without credit info
      setUserCredits(null);
      setShowBatchRegenerateModal(true);
    } finally {
      setIsCheckingCreditsForBatch(false);
    }
  };

  // Start batch regeneration process
  const startBatchRegeneration = async () => {
  // ...existing code...
    if (!csvData) {
      console.warn('❌ No CSV data available for batch regeneration');
      return;
    }

    setShowBatchRegenerateModal(false);
  setIsBatchRegenerating(true);

    // Mark that user has used AI generation in this session
  // ...existing code...

    const BATCH_SIZE = 50; // Process 50 items per batch
    const totalBatches = Math.ceil(csvData.rows.length / BATCH_SIZE);

    setBatchProgress({
      currentBatch: 0,
      totalBatches,
      processedItems: 0,
      totalItems: csvData.rows.length,
      successCount: 0,
      errorCount: 0,
      failedRows: []
    });

    try {
      // Get user's API key once
      const apiKeyService = (await import('../services/apiKeyService')).default;
      const { keys: apiKeys } = await apiKeyService.getApiKeys();

      const activeApiKey = apiKeys.find(k => k.is_active);
      if (!activeApiKey || !activeApiKey.api_key) {
        throw new Error('No active API key found. Please create an API key in your account settings.');
      }

      // Process batches sequentially
      for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
        const startIndex = batchIndex * BATCH_SIZE;
        const endIndex = Math.min(startIndex + BATCH_SIZE, csvData.rows.length);
        const batchRows = csvData.rows.slice(startIndex, endIndex);

        setBatchProgress(prev => prev ? { ...prev, currentBatch: batchIndex + 1 } : null);

        try {
          // Process each item in the batch
          const batchPromises = batchRows.map(async (row, indexInBatch) => {
            const globalIndex = startIndex + indexInBatch;

            try {
              const regenerateRequest = {
                title: row.title || 'Untitled',
                keywords: row.keywords ? row.keywords.split(',').map(k => k.trim()) : [],
                // albumId: null, // Remove albumId for CSV editor to use improve_meta_1 path
                customInstructions: regenerateSettings.customInstructions || undefined,
                settings: {
                  title_length: regenerateSettings.titleLength,
                  description_length: regenerateSettings.descriptionLength,
                  keywords_count: regenerateSettings.keywordsCount
                }
              };

              const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'https://api.metapic.app';

              // Create AbortController for timeout handling
              const controller = new AbortController();
              const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

              const response = await fetch(`${apiBaseUrl}/api/v1/improve-metadata`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'X-API-Key': activeApiKey.api_key
                },
                body: JSON.stringify(regenerateRequest),
                signal: controller.signal
              });

              clearTimeout(timeoutId);

              if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`HTTP ${response.status}: ${errorText}`);
              }

              const result = await response.json();

              if (!result.success) {
                throw new Error(result.error?.message || 'Regeneration failed');
              }

              const responseData = result.data?.data || result.data;
              const aiMetadata = responseData?.metadata;

              if (!aiMetadata || !aiMetadata.title || !aiMetadata.description || !aiMetadata.keywords) {
                throw new Error('Invalid response structure');
              }

              // Update CSV data with new metadata
              const newRows = [...csvData.rows];
              newRows[globalIndex] = {
                ...newRows[globalIndex],
                title: aiMetadata.title,
                description: aiMetadata.description,
                keywords: Array.isArray(aiMetadata.keywords)
                  ? aiMetadata.keywords.join(', ')
                  : aiMetadata.keywords
              };

              setCsvData({ ...csvData, rows: newRows });

              // Update progress for each individual item (more granular progress)
              setBatchProgress(prev => prev ? {
                ...prev,
                processedItems: prev.processedItems + 1,
                successCount: prev.successCount + 1
              } : null);

              // Mark as having unsaved changes after batch regeneration

              return { success: true, globalIndex };

            } catch (error: any) {
              let errorMessage = error.message;

              if (error.name === 'AbortError') {
                errorMessage = 'Request timeout (30s) - will retry in next batch';
              } else if (!navigator.onLine) {
                errorMessage = 'Network offline - will retry when back online';
              } else if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
                errorMessage = 'Network error - will retry in next batch';
              }

              // Update progress for failed item
              setBatchProgress(prev => prev ? {
                ...prev,
                processedItems: prev.processedItems + 1,
                errorCount: prev.errorCount + 1,
                failedRows: [...prev.failedRows, globalIndex]
              } : null);

              return { success: false, globalIndex, error: errorMessage };
            }
          });

          // Wait for all items in this batch to complete
          await Promise.allSettled(batchPromises);

          // Note: Individual progress updates above handle the granular progress
          // No need for batch-level progress update here

          // Small delay between batches to prevent overwhelming the API
          await new Promise(resolve => setTimeout(resolve, 1000));

        } catch (batchError: any) {
          console.error(`Batch ${batchIndex + 1} processing error:`, batchError);
          // Individual error handling already covers failed items
        }
      }

      // Show final results
      const finalProgress = batchProgress;
      if (finalProgress) {
        const { successCount, errorCount, failedRows } = finalProgress;

        if (errorCount === 0) {
          toast.success(`✅ ALL DONE! Successfully regenerated metadata for all ${successCount} files.`);
        } else if (successCount > 0) {
          toast.success(`✅ PARTIALLY DONE! ${successCount} files regenerated successfully. ${errorCount} files failed.`, {
            duration: 8000
          });

          // Show detailed error report for failed rows
          setTimeout(() => {
            const failedFilenames = failedRows.map(index => csvData.rows[index]?.filename).filter(Boolean);
            alert(`Failed files (${errorCount}):\n${failedFilenames.slice(0, 10).join('\n')}${failedFilenames.length > 10 ? '\n...and more' : ''}`);
          }, 1000);
        } else {
          toast.error(`❌ ALL FAILED! All ${errorCount} regenerations failed. Please check your connection and try again.`);
        }
      }

    } catch (error: any) {
      console.error('Batch regeneration failed:', error);
      toast.error(`❌ BATCH REGENERATION FAILED: ${error.message}`);
    } finally {
  // ...existing code...
      setIsBatchRegenerating(false);
      setBatchProgress(null);
    }
  };




  return (
    <Layout>
      {/* Progress Indicator - Hide when in editor step */}
      {currentStep !== 'editor' && (
        <div className="bg-gray-50 px-4 sm:px-6 lg:px-8 pt-8">
          <div className="max-w-4xl mx-auto mb-8">
            <div className="flex items-center justify-center">
              {[
                { step: 'upload', label: 'Upload', icon: Upload },
                { step: 'mapping', label: 'Map', icon: CheckCircle },
                { step: 'editor', label: 'Edit', icon: FileText }
              ].map(({ step, label, icon: Icon }, index) => (
                <React.Fragment key={step}>
                  <div className={`flex items-center ${
                    currentStep === step ? 'text-blue-600' : 'text-gray-400'
                  }`}>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      currentStep === step ? 'bg-blue-600 text-white' : 'bg-gray-200'
                    }`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <span className="ml-2 font-medium">{label}</span>
                  </div>
                  {index < 2 && (
                    <div className={`w-16 h-0.5 mx-4 ${
                      ['mapping', 'editor'].includes(currentStep) ? 'bg-blue-600' : 'bg-gray-200'
                    }`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        {/* Content */}
        {currentStep === 'upload' && (
          <UploadStep
            fileInputRef={fileInputRef}
            onFileUpload={handleFileUpload}
            isProcessing={isProcessing}
          />
        )}
        {currentStep === 'mapping' && (
          <MappingStep
            csvData={csvData}
            columnMapping={columnMapping}
            onColumnMappingChange={setColumnMapping}
            onContinue={handleMappingComplete}
            onBack={() => setCurrentStep('upload')}
            isProcessing={isProcessing}
            hasVisitedEditor={hasVisitedEditor}
          />
        )}
        {currentStep === 'editor' && (
          <EditorStep
            csvData={csvData}
            regeneratingIndices={regeneratingIndices}
            isBatchRegenerating={isBatchRegenerating}
            isCheckingCreditsForBatch={isCheckingCreditsForBatch}
            isCheckingCreditsForIndividual={isCheckingCreditsForIndividual}
            onExportClick={handleExportClick}
            onBatchRegenerateClick={checkCreditsAndShowModal}
            onUpdateCSVData={handleUpdateCSVData}
            onBatchUpdateAll={handleBatchUpdateAll}
            onRegenerate={handleRegenerate}
          />
        )}
      </div>

      {/* Modals */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        csvData={csvData}
        exportFieldSelection={exportFieldSelection}
        onFieldSelectionChange={setExportFieldSelection}
        onExport={handleExportCSV}
        userCredits={userCredits}
        isExporting={isExporting}
        onCheckCredits={async () => {
          // Check credits for export
          try {
            setIsCheckingCreditsForExport(true);
            const apiKeyService = (await import('../services/apiKeyService')).default;
            const { keys: apiKeys } = await apiKeyService.getApiKeys();

            const activeApiKey = apiKeys.find(k => k.is_active);
            if (!activeApiKey || !activeApiKey.api_key) {
              toast.error('No active API key found. Please create an API key in your account settings.');
              return;
            }

            const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'https://api.metapic.app';

            let response;
            response = await fetch(`${apiBaseUrl}/api/v1/credits/balance`, {
              headers: {
                'X-API-Key': activeApiKey.api_key,
                'Content-Type': 'application/json'
              }
            });

            if (!response.ok) {
              response = await fetch(`${apiBaseUrl}/api/v1/credits/balance`, {
                headers: {
                  [activeApiKey.api_key]: '',
                  'Content-Type': 'application/json'
                }
              });
            }

            if (!response.ok) {
              response = await fetch(`${apiBaseUrl}/credits/balance`, {
                headers: {
                  'Authorization': `Bearer ${activeApiKey.api_key}`,
                  'Content-Type': 'application/json'
                }
              });
            }

            if (response.ok) {
              const creditData = await response.json();
              let balance = 0;
              if (creditData.data?.data?.balance !== undefined) {
                balance = creditData.data.data.balance;
              } else if (creditData.data?.balance !== undefined) {
                balance = creditData.data.balance;
              } else if (creditData.balance !== undefined) {
                balance = creditData.balance;
              }
              setUserCredits(balance);
            }
          } catch (error) {
            console.error('Failed to check credits:', error);
            } finally {
              setIsCheckingCreditsForExport(false);
            }
        }}
      />

      <RegenerateModal
        isOpen={showRegenerateSettings}
        onClose={() => {
          setShowRegenerateSettings(false);
          setPendingRegenerateIndex(null);
        }}
        onConfirm={handleConfirmRegenerate}
        titleLength={regenerateSettings.titleLength}
        onTitleLengthChange={(value) => setRegenerateSettings(prev => ({ ...prev, titleLength: value }))}
        descriptionLength={regenerateSettings.descriptionLength}
        onDescriptionLengthChange={(value) => setRegenerateSettings(prev => ({ ...prev, descriptionLength: value }))}
        keywordsCount={regenerateSettings.keywordsCount}
        onKeywordsCountChange={(value) => setRegenerateSettings(prev => ({ ...prev, keywordsCount: value }))}
        customInstructions={regenerateSettings.customInstructions}
        onCustomInstructionsChange={(value) => setRegenerateSettings(prev => ({ ...prev, customInstructions: value }))}
      />

      <BatchRegenerateModal
        isOpen={showBatchRegenerateModal}
        onClose={() => setShowBatchRegenerateModal(false)}
        onStartBatch={startBatchRegeneration}
        csvData={csvData}
        userCredits={userCredits}
        isOnline={isOnline}
        titleLength={regenerateSettings.titleLength}
        onTitleLengthChange={(value) => setRegenerateSettings(prev => ({ ...prev, titleLength: value }))}
        descriptionLength={regenerateSettings.descriptionLength}
        onDescriptionLengthChange={(value) => setRegenerateSettings(prev => ({ ...prev, descriptionLength: value }))}
        keywordsCount={regenerateSettings.keywordsCount}
        onKeywordsCountChange={(value) => setRegenerateSettings(prev => ({ ...prev, keywordsCount: value }))}
        customInstructions={regenerateSettings.customInstructions}
        onCustomInstructionsChange={(value) => setRegenerateSettings(prev => ({ ...prev, customInstructions: value }))}
      />

      <BatchProgressModal
        isOpen={isBatchRegenerating}
        progress={batchProgress}
        isOnline={isOnline}
      />
    </Layout>
  );
};

export default CSVEditorPage;
